import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Abas,
  Alerta,
  Bloco,
  HeroModulo,
  Campo,
  CAMPO_SIM,
  Cascata,
  Casos,
  type CasoPratico,
  Linha,
  Referencias,
  Tabela,
  copiar,
} from "../componentes/HubDpUi";
import { reais, type ParametrosFolha } from "../lib/folhaDp";
import {
  calcularEncargos,
  calcularRescisao,
  RESCISAO,
  type MotivoRescisao,
  type Regime,
} from "../lib/encargosDp";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import { REFERENCIA_ENCARGOS } from "../componentes/HubDpTextos";

// O módulo Encargos e FGTS: o que a empresa paga além do salário, e o que o
// desligamento custa.

const BASE = "/ferramenta/hub-dp/encargos";
const ABAS = [
  { slug: "", rotulo: "Encargos patronais" },
  { slug: "rescisao", rotulo: "FGTS na rescisão" },
  { slug: "referencia", rotulo: "Referência" },
  { slug: "casos", rotulo: "Casos práticos" },
];

interface Form {
  folha: string;
  receitaBruta: string;
  regime: Regime;
  cprbPercentual: string;
  ratPercentual: string;
  fap: string;
  terceirosPercentual: string;
  incluirFgts: boolean;
  saldoFgts: string;
  motivo: MotivoRescisao;
}

const VAZIO: Form = {
  folha: "",
  receitaBruta: "",
  regime: "folha",
  cprbPercentual: "4,5",
  ratPercentual: "2",
  fap: "1,0",
  terceirosPercentual: "5,8",
  incluirFgts: true,
  saldoFgts: "",
  motivo: "semJustaCausa",
};

const num = (t: string): number => mascararMoeda(t).numero ?? 0;
/** Percentual digitado com vírgula: "5,8" → 5.8. */
const pct = (t: string): number => Number(t.replace(",", ".").replace(/[^0-9.]/g, "")) || 0;
/** Máscara de percentual: dígitos e uma vírgula, no máximo uma casa. */
const mascararPct = (t: string): string => {
  const limpo = t.replace(/[^0-9,]/g, "").replace(/,+/g, ",");
  const [inteiro = "", casa = ""] = limpo.split(",");
  return limpo.includes(",") ? `${inteiro.slice(0, 2)},${casa.slice(0, 2)}` : inteiro.slice(0, 3);
};

const CASOS: CasoPratico<Partial<Form>>[] = [
  {
    nome: "Comércio, 10 funcionários",
    descricao: "Folha de R$ 35.000, RAT grau 2 com FAP 1,0, terceiros 5,8%. O custo típico do regime de 20%.",
    valores: { folha: moedaDeNumero(35000), regime: "folha", ratPercentual: "2", fap: "1,0" },
  },
  {
    nome: "Empresa de TI na CPRB",
    descricao:
      "Folha de R$ 80.000 e receita de R$ 500.000 a 4,5%. Repare que RAT, terceiros e FGTS CONTINUAM sobre a folha — a desoneração só substitui os 20%.",
    valores: {
      folha: moedaDeNumero(80000),
      receitaBruta: moedaDeNumero(500000),
      regime: "cprb",
      cprbPercentual: "4,5",
      ratPercentual: "1",
      fap: "0,8",
    },
  },
  {
    nome: "O mesmo caso no regime de folha",
    descricao: "A mesma folha e a mesma receita, mas sem a CPRB. Compare os totais: a desoneração nem sempre é vantajosa.",
    valores: {
      folha: moedaDeNumero(80000),
      receitaBruta: moedaDeNumero(500000),
      regime: "folha",
      ratPercentual: "1",
      fap: "0,8",
    },
  },
  {
    nome: "RAT grau 3 com FAP no máximo",
    descricao: "Folha de R$ 35.000, grau de risco 3 e FAP 2,0. O RAT efetivo vira 6% — o dobro do nominal, e é legal.",
    valores: { folha: moedaDeNumero(35000), regime: "folha", ratPercentual: "3", fap: "2,0" },
  },
  {
    nome: "Rescisão sem justa causa",
    descricao: "Saldo de R$ 18.500 na conta vinculada. Multa de 40% e saque integral.",
    valores: { saldoFgts: moedaDeNumero(18500), motivo: "semJustaCausa" },
  },
  {
    nome: "Acordo do art. 484-A",
    descricao:
      "O mesmo saldo, com acordo entre as partes. Multa de 20% e saque de 80% — a única hipótese com saque parcial.",
    valores: { saldoFgts: moedaDeNumero(18500), motivo: "acordoMutuo" },
  },
];

export default function DpEncargos({ p, aba }: { p: ParametrosFolha; aba: string }) {
  const [f, setF] = useState<Form>(VAZIO);
  const [copiado, setCopiado] = useState(false);
  const navegar = useNavigate();
  const troca = <K extends keyof Form>(c: K, v: Form[K]) => setF((a) => ({ ...a, [c]: v }));

  const folha = num(f.folha);
  const temFolha = folha > 0;
  const entrada = {
    folha,
    receitaBruta: num(f.receitaBruta),
    regime: f.regime,
    cprbPercentual: pct(f.cprbPercentual),
    ratPercentual: pct(f.ratPercentual),
    fap: pct(f.fap),
    terceirosPercentual: pct(f.terceirosPercentual),
    incluirFgts: f.incluirFgts,
  };
  const r = temFolha ? calcularEncargos(entrada) : null;
  // A comparação com o OUTRO regime, sempre — é o que responde a pergunta que o
  // contador faz de verdade: "vale a pena optar?".
  const outro = temFolha
    ? calcularEncargos({ ...entrada, regime: f.regime === "cprb" ? "folha" : "cprb" })
    : null;

  const saldo = num(f.saldoFgts);
  const resc = saldo > 0 ? calcularRescisao(saldo, f.motivo) : null;

  return (
    <>
      <HeroModulo
        titulo="Encargos e FGTS"
        subtitulo="O que a empresa paga além do salário, e quanto custa o desligamento."
        heroi={
          aba === "rescisao" && resc
            ? {
                rotulo: "Multa rescisória",
                valor: reais(resc.multa),
                detalhe: `${resc.regra.rotulo} — saque de ${reais(resc.saqueDisponivel)}`,
              }
            : r
              ? {
                  rotulo: "Custo sobre a folha",
                  valor: r.semFolha ? "—" : `${r.custoSobreFolhaPercentual.toLocaleString("pt-BR")}%`,
                  detalhe: `${reais(r.total)} de encargos sobre ${reais(folha)} de folha`,
                }
              : null
        }
        vazio="Informe a folha total do mês e o custo aparece aqui."
      />
      <Abas base={BASE} abas={ABAS} />

      {aba === "" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_1fr]">
          <form id="dados-dos-encargos" className="surface h-fit space-y-5 p-5 lg:sticky lg:top-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Folha total do mês" ajuda="Soma dos salários brutos. É a base do INSS patronal, do RAT, dos terceiros e do FGTS.">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.folha}
                onChange={(e) => troca("folha", mascararMoeda(e.target.value).texto)}
              />
            </Campo>

            <fieldset>
              <legend className="mb-1.5 text-xs font-semibold text-cinza">Regime previdenciário</legend>
              <div className="space-y-2">
                {(
                  [
                    ["folha", "Folha — 20% sobre a folha", "O regime geral (Lei 8.212/91, art. 22, I)."],
                    [
                      "cprb",
                      "CPRB — alíquota sobre a receita",
                      "A desoneração substitui APENAS os 20%. RAT, terceiros e FGTS continuam sobre a folha.",
                    ],
                  ] as const
                ).map(([v, rotulo, explica]) => (
                  <label key={v} className="flex gap-2.5 text-sm text-tinta">
                    <input
                      type="radio"
                      name="regime-prev"
                      className="mt-0.5 size-4 shrink-0 accent-laranja"
                      checked={f.regime === v}
                      onChange={() => troca("regime", v)}
                    />
                    <span>
                      <span className="font-semibold">{rotulo}</span>
                      <span className="block text-xs text-cinza">{explica}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {f.regime === "cprb" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rotulo="Receita bruta do mês">
                  <input
                    className={CAMPO_SIM}
                    inputMode="numeric"
                    placeholder="R$ 0,00"
                    value={f.receitaBruta}
                    onChange={(e) => troca("receitaBruta", mascararMoeda(e.target.value).texto)}
                  />
                </Campo>
                <Campo rotulo="Alíquota da CPRB (%)" ajuda="Varia por atividade: 1,0 a 4,5. Consulte a Lei 12.546/2011.">
                  <input
                    className={CAMPO_SIM}
                    inputMode="decimal"
                    value={f.cprbPercentual}
                    onChange={(e) => troca("cprbPercentual", mascararPct(e.target.value))}
                  />
                </Campo>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-3">
              <Campo rotulo="RAT (%)" ajuda="Grau de risco da atividade: 1, 2 ou 3.">
                <select
                  className={CAMPO_SIM}
                  value={f.ratPercentual}
                  onChange={(e) => troca("ratPercentual", e.target.value)}
                >
                  <option value="1">1 — risco leve</option>
                  <option value="2">2 — risco médio</option>
                  <option value="3">3 — risco grave</option>
                </select>
              </Campo>
              <Campo rotulo="FAP" ajuda="Fator Acidentário de Prevenção, de 0,5 a 2,0. Multiplica o RAT.">
                <input
                  className={CAMPO_SIM}
                  inputMode="decimal"
                  value={f.fap}
                  onChange={(e) => troca("fap", mascararPct(e.target.value))}
                />
              </Campo>
              <Campo rotulo="Terceiros (%)" ajuda="Soma das contribuições ao Sistema S. Varia pelo código FPAS — em torno de 5,8%.">
                <input
                  className={CAMPO_SIM}
                  inputMode="decimal"
                  value={f.terceirosPercentual}
                  onChange={(e) => troca("terceirosPercentual", mascararPct(e.target.value))}
                />
              </Campo>
            </div>

            <label className="flex items-center gap-2.5 text-sm text-tinta">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={f.incluirFgts}
                onChange={(e) => troca("incluirFgts", e.target.checked)}
              />
              Incluir o FGTS de 8%
            </label>
          </form>

          <div id="resultado" className="space-y-4">
            {!r ? (
              <div className="rounded-cf border border-dashed border-borda p-6 text-sm leading-relaxed text-cinza">
                Informe a folha total do mês para ver os encargos.
              </div>
            ) : (
              <>
                <Bloco titulo="Encargos do mês" destaque>
                  <Linha
                    rotulo={f.regime === "cprb" ? "CPRB" : "INSS patronal 20%"}
                    valor={reais(r.inssPatronal)}
                    ajuda={
                      f.regime === "cprb"
                        ? `${f.cprbPercentual}% sobre a receita de ${reais(r.basePrevidenciaria)}`
                        : `sobre a folha de ${reais(r.basePrevidenciaria)}`
                    }
                  />
                  <Linha
                    rotulo={`RAT × FAP (${r.ratEfetivoPercentual}%)`}
                    valor={reais(r.rat)}
                    ajuda={`${f.ratPercentual} × ${f.fap} — sempre sobre a FOLHA`}
                  />
                  <Linha rotulo="Terceiros" valor={reais(r.terceiros)} ajuda="sempre sobre a FOLHA" />
                  {r.fgts > 0 && <Linha rotulo="FGTS 8%" valor={reais(r.fgts)} ajuda="sempre sobre a FOLHA" />}
                  <Linha rotulo="Total de encargos" valor={reais(r.total)} tom="total" />
                </Bloco>

                {/* A cascata no lugar de repetir o número do hero: o percentual já
                    está lá em cima, e o que falta é VER de que ele é feito. */}
                <Bloco titulo="De que o encargo é feito">
                  <Cascata
                    rotuloTotal="Total de encargos"
                    total={r.total}
                    fatias={[
                      {
                        rotulo: f.regime === "cprb" ? "CPRB" : "INSS patronal 20%",
                        valor: r.inssPatronal,
                        cor: "azul",
                        detalhe: f.regime === "cprb" ? "sobre a receita" : "sobre a folha",
                      },
                      { rotulo: "RAT × FAP", valor: r.rat, cor: "laranja", detalhe: "sobre a folha" },
                      { rotulo: "Terceiros", valor: r.terceiros, cor: "cinza", detalhe: "sobre a folha" },
                      { rotulo: "FGTS", valor: r.fgts, cor: "sucesso", detalhe: "sobre a folha" },
                    ]}
                  />
                  <p className="mt-3 border-t border-borda pt-2.5 text-xs leading-relaxed text-cinza">
                    Cada R$ 1,00 de salário custa{" "}
                    <b className="text-tinta">
                      R$&nbsp;
                      {(1 + r.custoSobreFolhaPercentual / 100).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </b>{" "}
                    à empresa. No regime de 20% o típico fica entre 36% e 40%.
                  </p>
                </Bloco>

                {outro && (
                  <Bloco titulo="E no outro regime?">
                    <Linha
                      rotulo={f.regime === "cprb" ? "No regime de folha (20%)" : "Na CPRB"}
                      valor={reais(outro.total)}
                    />
                    <Linha rotulo="Diferença" valor={reais(Math.abs(outro.total - r.total))} />
                    <p className="mt-2 text-xs leading-relaxed text-cinza">
                      {outro.total > r.total
                        ? "O regime escolhido está mais barato."
                        : outro.total < r.total
                          ? "O outro regime sairia mais barato neste mês."
                          : "Os dois custam o mesmo neste mês."}{" "}
                      A opção pela CPRB é <strong>anual e irretratável</strong> (Lei 12.546/2011) —
                      compare o ano inteiro, não um mês.
                    </p>
                    {f.regime === "cprb" && num(f.receitaBruta) === 0 && (
                      <p className="mt-2 text-xs text-erro-tinta">
                        Sem receita informada, a CPRB sai zero e a comparação não vale nada.
                        Preencha a receita bruta.
                      </p>
                    )}
                  </Bloco>
                )}

                <Alerta tom="atencao" titulo="O que a CPRB NÃO substitui">
                  Só o INSS patronal de 20%. O RAT × FAP, os terceiros e o FGTS continuam incidindo
                  sobre a folha — zerar tudo na CPRB subestima o custo, e é o erro mais comum deste
                  módulo.
                </Alerta>
              </>
            )}
          </div>
        </div>
      )}

      {aba === "rescisao" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_1fr]">
          <form id="dados-da-rescisao" className="surface h-fit space-y-5 p-5 lg:sticky lg:top-6" onSubmit={(e) => e.preventDefault()}>
            <Campo
              rotulo="Saldo do FGTS"
              ajuda="O total depositado na conta vinculada ao longo do contrato — não o salário nem o último depósito."
            >
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.saldoFgts}
                onChange={(e) => troca("saldoFgts", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
            <Campo rotulo="Motivo do desligamento">
              <select
                className={CAMPO_SIM}
                value={f.motivo}
                onChange={(e) => troca("motivo", e.target.value as MotivoRescisao)}
              >
                {(Object.keys(RESCISAO) as MotivoRescisao[]).map((m) => (
                  <option key={m} value={m}>
                    {RESCISAO[m].rotulo}
                  </option>
                ))}
              </select>
            </Campo>
          </form>

          <div id="resultado" className="space-y-4">
            {!resc ? (
              <div className="rounded-cf border border-dashed border-borda p-6 text-sm leading-relaxed text-cinza">
                Informe o saldo da conta vinculada para ver a multa e o saque.
              </div>
            ) : (
              <>
                <Bloco titulo={resc.regra.rotulo} destaque>
                  <Linha rotulo="Saldo na conta vinculada" valor={reais(resc.saldo)} />
                  <Linha
                    rotulo={`Multa rescisória (${resc.regra.multaPercentual}%)`}
                    valor={reais(resc.multa)}
                    ajuda="paga pelo empregador, depositada na conta do trabalhador"
                  />
                  <Linha rotulo="Disponível para saque" valor={reais(resc.saqueDisponivel)} />
                  {resc.permaneceNaConta > 0 && (
                    <Linha
                      rotulo="Permanece na conta"
                      valor={reais(resc.permaneceNaConta)}
                      ajuda="fica retido, saca em outra hipótese legal"
                    />
                  )}
                </Bloco>

                <Bloco titulo="O que mais muda com este motivo">
                  <Linha rotulo="Aviso prévio" valor={resc.regra.avisoPrevio} />
                  <Linha
                    rotulo="Seguro-desemprego"
                    valor={resc.regra.seguroDesemprego ? "sim, se elegível" : "não"}
                  />
                  <Linha rotulo="Base legal" valor={resc.regra.base} />
                </Bloco>

                <Bloco titulo="Custo do desligamento para a empresa" destaque>
                  <p className="font-display text-3xl font-bold text-titulo tabular-nums">
                    {reais(resc.custoEmpregador)}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-cinza">
                    Só a multa do FGTS. Aviso prévio indenizado, férias e 13º proporcionais e demais
                    verbas rescisórias entram por fora — este módulo cobre o FGTS.
                  </p>
                </Bloco>

                <button
                  type="button"
                  className="pill-cta"
                  onClick={async () => {
                    const texto = [
                      `FGTS na rescisão — ${resc.regra.rotulo}`,
                      `Saldo na conta vinculada: ${reais(resc.saldo)}`,
                      `Multa de ${resc.regra.multaPercentual}%: ${reais(resc.multa)}`,
                      `Disponível para saque: ${reais(resc.saqueDisponivel)}`,
                      `Permanece na conta: ${reais(resc.permaneceNaConta)}`,
                      `Aviso prévio: ${resc.regra.avisoPrevio}`,
                      `Seguro-desemprego: ${resc.regra.seguroDesemprego ? "sim, se elegível" : "não"}`,
                      `Base legal: ${resc.regra.base}`,
                      "",
                      "Gerado pelo HUB DP - Contabilidade Facilitada (ferramenta educacional).",
                    ].join("\n");
                    setCopiado(await copiar(texto));
                  }}
                >
                  {copiado ? "Copiado" : "Copiar o resumo"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {aba === "referencia" && (
        <div className="space-y-6">
          {REFERENCIA_ENCARGOS.map((t) => (
            <Tabela key={t.titulo} tabela={t} />
          ))}
          <Alerta tom="erro" titulo="O FGTS não é desconto do empregado">
            Os 8% são depósito do empregador. Aparecem como custo da empresa, nunca no recibo de
            descontos do trabalhador.
          </Alerta>
          <Alerta tom="atencao" titulo="RAT sem FAP é conta incompleta">
            O FAP multiplica o RAT e vai de 0,5 a 2,0 — pode metade ou dobrar o encargo. Ele é
            divulgado anualmente por CNPJ; usar o RAT nominal sozinho erra em uma das duas direções.
          </Alerta>
          <Alerta tom="informacao" titulo="Terceiros variam por FPAS">
            Os {pct(f.terceirosPercentual).toLocaleString("pt-BR")}% do campo são um valor típico.
            Cada atividade tem sua composição (SESI, SENAI, SEBRAE, INCRA, salário-educação) —
            confira o código FPAS da empresa antes de fechar a guia.
          </Alerta>
        </div>
      )}

      {aba === "casos" && (
        <Casos
          casos={CASOS}
          aoCarregar={(v) => {
            setF({ ...VAZIO, ...v });
            navegar(v.saldoFgts ? `${BASE}/rescisao` : BASE);
          }}
        />
      )}

      <Referencias inss={p.referenciaInss} irrf={p.referenciaIrrf} />
    </>
  );
}
